import { useState, useCallback, useRef } from 'react'
import _ from 'lodash'
import {
  createAiVideo,
  getAiVideoById,
  updateAiVideo
} from '../../../services/api/aiVideoGeneration'
import history from '../../../history'
import {
  clearSceneCurrentAudio,
  normalizeSceneAudioHistory
} from '../utils/audioHistory'
import { LANGUAGE_MAP } from '../constants/videoLanguages'

export const defaultData = {
  file: null,
  voiceoverText: '',
  audioMode: 'narration',
  voiceoverAudio: null,
  videoStyle: {
    key: 'realistic'
  },
  creativeDirection: {
    sessionId: null,
    currentPhase: 'vision',
    currentMovement: 'the_read',
    movementsCovered: [],
    movementsTotal: 8,
    phaseData: {},
    finalized: false,
    bible: null,
    messages: [],
    uploadedReferences: []
  },
  shotGenerationStyle: null,
  editingStyle: false,
  aspectRatio: '16:9',
  selectedCharacter: null,
  characterList: [],
  characterMap: null,
  shotList: [],
  calculatedShotList: null,
  shotLoading: false,
  locationMap: null,
  locationReference: null,
  propMap: null,
  props_reference: null,
  selectedVoice: null,
  language: 'en',
  audio: {
    id: null,
    status: 'idle' // 'idle' | 'pending' | 'completed' | 'failed'
  },
  video: {
    id: null,
    status: 'idle' // 'idle' | 'pending' | 'completed' | 'failed'
  },
  draft: {
    id: null,
    status: 'pending' // 'completed' || 'failed' || 'pending'
  },
  outfitMap: null,
  propMap: null,
  props: null,
  wardrobe: null,
  outfits_reference: null,
  outfits: [],
  final_videos_history: [],
  timelineProjectId: null,
  characterVoices: {},
  videoGenerationMode: 'manual',
  aiVideoFlowMode: null
}

const normalizeAudioMode = mode => {
  if (mode === 'advanced') return 'dialogue'

  if (
    mode === 'narration' ||
    mode === 'dialogue' ||
    mode === 'dialogue_and_narration'
  ) {
    return mode
  }

  return 'narration'
}

const isReferenceResolved = reference =>
  Boolean(
    reference &&
      (reference.uri ||
        reference.url ||
        reference.image ||
        reference.src)
  )

/**
 * Custom hook to manage video data persistence (create, read, update)
 * Handles fetching, saving, and cloning video data with auto-save functionality
 */
export const useVideoDataPersistence = ({
  videoId,
  formData,
  setFormData,
  setStepsValidation,
  steps,
  currentStep,
  onLocationMapGenerate
} = {}) => {
  const [isFetchingVideoData, setIsFetchingVideoData] = useState(false)
  const [isSavingVideoData, setIsSavingVideoData] = useState(false)
  const [isCloning, setIsCloning] = useState(false)
  const [error, setError] = useState(null)
  const saveInProgress = useRef(false)

  /**
   * Safe JSON parsing with nested string support
   */
  const parseJSONSafely = useCallback((val, maxDepth = 5) => {
    if (val === null || val === undefined) return null
    let result = val
    let depth = 0

    while (typeof result === 'string' && depth < maxDepth) {
      try {
        result = JSON.parse(result)
        depth++
      } catch {
        break
      }
    }
    return result
  }, [])

  /**
   * Normalize a value to a default if it's null, undefined, or the string "null"
   * This prevents crashes when API returns "null" strings instead of proper objects/arrays
   */
  const normalizeValue = useCallback((val, defaultValue) => {
    if (val === null || val === undefined || val === 'null') {
      return defaultValue
    }
    return val
  }, [])

  /**
   * Prepare API payload from form data
   */
  const getPayload = useCallback(data => {
    const location_map = data.locationMap
      ? JSON.stringify(data.locationMap)
      : ''
    const location_reference = data.locationReference
      ? JSON.stringify(data.locationReference)
      : ''
    const character_map = data.characterMap
      ? JSON.stringify(data.characterMap)
      : ''
    const outfit_map = data.outfitMap ? JSON.stringify(data.outfitMap) : ''
    const prop_map = data.propMap ? JSON.stringify(data.propMap) : ''
    const props = data.props ? JSON.stringify(data.props) : ''
    const wardrobe = data.wardrobe ? JSON.stringify(data.wardrobe) : ''
    const props_reference = data.props_reference
      ? JSON.stringify(data.props_reference)
      : ''
    const outfits_reference = data.outfits_reference
      ? JSON.stringify(data.outfits_reference)
      : ''
    const final_videos_history = data.final_videos_history
      ? JSON.stringify(data.final_videos_history)
      : ''

    const transitions = data.transitions ? JSON.stringify(data.transitions) : ''
    const character_voices = data.characterVoices
      ? JSON.stringify(data.characterVoices)
      : ''
    const videoCreationMode =
      data.aiVideoFlowMode ||
      data.creativeDirection?.flowMode ||
      data.creativeDirection?.flow_mode ||
      null
    const creativeGuidelineSessionId =
      data.creativeDirection?.sessionId ||
      data.creativeDirection?.session_id ||
      data.creativeDirection?.creative_guideline_session_id ||
      data.creativeDirection?.creativeGuidelineSessionId ||
      null
    const creativeDirectionPayload = data.creativeDirection
      ? {
          ...data.creativeDirection,
          flowMode: videoCreationMode
        }
      : null
    const creative_direction = creativeDirectionPayload
      ? JSON.stringify(creativeDirectionPayload)
      : ''

    return {
      voiceoverText: data.voiceoverText,
      video_generation_mode: data.videoGenerationMode || null,
      video_creation_mode: videoCreationMode,
      audio_mode: data.audioMode || 'narration',
      voiceoverAudio: data.voiceoverAudio,
      videoStyle: data.videoStyle,
      creative_direction,
      creative_guideline_session_id: creativeGuidelineSessionId,
      shotGenerationStyle: (() => {
        const id =
          data.shotGenerationStyle?.id ||
          data.shotGenerationStyle?.raw?.id ||
          data.shotGenerationStyle?.key ||
          (typeof data.shotGenerationStyle === 'string'
            ? data.shotGenerationStyle
            : null)
        return id === 'default_style' ? null : id
      })(),
      editingStyle: data.editingStyle,
      aspectRatio: data.aspectRatio || '16:9',
      selectedCharacter: data.selectedCharacter,
      shotList: (data.shotList || []).map(normalizeSceneAudioHistory),
      video_url: data.finalVideo || '',
      location_map,
      location_reference,
      character_map,
      props_reference,
      selected_voice: data.selectedVoice,
      lang: data.language || 'en',
      audio: data.audio,
      video: data.video,
      draft: data.draft,
      outfit_map,
      prop_map,
      props,
      wardrobe,
      outfits_reference,
      outfits: data.outfits || [],
      final_videos_history,
      timeline_project_id: data.timelineProjectId || null,
      transitions,
      character_voices,
      file: null
    }
  }, [])

  /**
   * Flatten shots for API response format
   */
  const flatShots = useCallback(input => {
    if (!input) return {}
    return input.reduce((acc, scene) => {
      scene.shots.forEach(shot => {
        const keyToUse = shot.key || shot.id
        const formattedKey = keyToUse.replace(/\s+/g, '-')
        acc[formattedKey] = shot
      })
      return acc
    }, {})
  }, [])

  /**
   * Fetch video data by ID
   */
  const fetchVideoData = useCallback(async () => {
    if (isFetchingVideoData || !videoId) return

    try {
      setIsFetchingVideoData(true)
      setError(null)
      const response = await getAiVideoById(videoId)
      const data = response && response.data

      // Parse and normalize to prevent "null" string or null values from crashing components
      const locationMap = normalizeValue(
        parseJSONSafely(data && data.location_map),
        {}
      )
      const locationReference = normalizeValue(
        parseJSONSafely(data && data.location_reference),
        {}
      )
      const props_reference = normalizeValue(
        parseJSONSafely(data && data.props_reference),
        {}
      )
      const characterMap = normalizeValue(
        parseJSONSafely(data && data.character_map),
        {}
      )
      const selectedCharacter = normalizeValue(
        parseJSONSafely(data && data.selected_character),
        []
      )
      const characterList = normalizeValue(
        parseJSONSafely(data && data.character_list),
        []
      )
      const outfitMap = normalizeValue(
        parseJSONSafely(data && data.outfit_map),
        {}
      )
      const propMap = normalizeValue(parseJSONSafely(data && data.prop_map), {})
      const props = normalizeValue(parseJSONSafely(data && data.props), {})
      const wardrobe = normalizeValue(
        parseJSONSafely(data && data.wardrobe),
        {}
      )
      const outfits_reference = normalizeValue(
        parseJSONSafely(data && data.outfits_reference),
        {}
      )
      const outfits = normalizeValue(parseJSONSafely(data && data.outfits), [])
      const finalVideosHistory = normalizeValue(
        parseJSONSafely(data && data.final_videos_history),
        []
      )
      const transitions = normalizeValue(
        parseJSONSafely(data && data.transitions),
        {}
      )
      const characterVoices = normalizeValue(
        parseJSONSafely(data && data.character_voices),
        {}
      )
      const creativeDirection = normalizeValue(
        parseJSONSafely(data && data.creative_direction),
        defaultData.creativeDirection
      )
      const aiVideoFlowMode =
        data.video_creation_mode ||
        data.videoCreationMode ||
        null

      setFormData(prev => ({
        ...prev,
        shotList: (data.shot_list || []).map(scene =>
          normalizeSceneAudioHistory({
            ...scene,
            shots: (scene.shots || []).map(shot => ({
              ...shot
            }))
          })
        ),
        calculatedShotList: data.calculated_shot_list || null,
        voiceoverText: data.voiceover_text,
        audioMode: normalizeAudioMode(data.audio_mode || data.audioMode),
        videoStyle: data.video_style,
        creativeDirection: {
          ...defaultData.creativeDirection,
          ...creativeDirection,
          flowMode: aiVideoFlowMode,
          sessionId:
            creativeDirection?.sessionId ||
            creativeDirection?.session_id ||
            creativeDirection?.creative_guideline_session_id ||
            creativeDirection?.creativeGuidelineSessionId ||
            data.creative_guideline_session_id ||
            data.creativeGuidelineSessionId ||
            data.creative_direction_session_id ||
            null
        },
        aspectRatio: data.aspect_ratio || '16:9',
        shotsApiRes: flatShots(data.shot_list),
        finalVideo: data.video_url || '',
        locationMap,
        locationReference,
        props_reference,
        characterMap,
        selectedCharacter,
        characterList,
        selectedVoice: data.selected_voice,
        language: data.lang || data.language || data.lng || 'en',
        audio: data.audio || defaultData.audio,
        video: data.video || defaultData.video,
        draft: data.draft || defaultData.draft,
        outfitMap,
        propMap,
        props,
        wardrobe,
        outfits_reference,
        outfits,
        final_videos_history: finalVideosHistory,
        timelineProjectId:
          data.timeline_project_id || data.timelineProjectId || null,
        transitions,
        characterVoices,
        videoGenerationMode: data.video_generation_mode || 'manual',
        aiVideoFlowMode,
        shotGenerationStyle: data.shot_generation_style
          ? {
            id: data.shot_generation_style,
            key: data.shot_generation_style
          }
          : null
      }))

      setStepsValidation({
        uploadDocument: Boolean(data.voiceover_text),
        narrationVoiceover: Boolean(data.selected_voice),
        characterVoiceover: true,
        videoStyle: Boolean(data.video_style),
        creativeDirection: true,
        shotList:
          data.shot_list &&
          data.shot_list.length > 0 &&
          (() => {
            const charKeys = characterMap ? Object.keys(characterMap) : []
            const locKeys = locationMap ? Object.keys(locationMap) : []

            const allCharsMapped = charKeys.every(key =>
              selectedCharacter.some(c => c.original === key || c.last === key)
            )

            const allLocsGenerated = locKeys.every(
              key =>
                locationReference &&
                locationReference[key] &&
                isReferenceResolved(locationReference[key])
            )

            const propKeys = propMap ? Object.keys(propMap) : []
            const allPropsGenerated = propKeys.every(
              key =>
                props_reference &&
                props_reference[key] &&
                isReferenceResolved(props_reference[key])
            )

            return allCharsMapped && allLocsGenerated && allPropsGenerated
          })(),
        imageGeneration:
          data.shot_list &&
          data.shot_list.length > 0 &&
          data.shot_list.every(
            scene =>
              scene.shots.length > 0 && scene.shots.every(shot => shot.imageSrc)
          ),
        videoGeneration:
          data.shot_list &&
          data.shot_list.length > 0 &&
          data.shot_list.every(scene => scene.shots.length > 0) &&
          data.shot_list.every(scene =>
            scene.shots.every(
              shot => shot.vidSrc || (shot.is_static && shot.imageSrc)
            )
          )
      })

      // Trigger location generation if needed
      // Skip if likely going to draft mode (pending draft exists)
      const hasPendingDraft =
        data.draft && data.draft.id && data.draft.status === 'pending'

      const isLocationReferenceEmpty =
        !locationReference ||
        (typeof locationReference === 'object' &&
          Object.keys(locationReference).length === 0)

      if (
        !hasPendingDraft &&
        isLocationReferenceEmpty &&
        locationMap &&
        Object.keys(locationMap).length > 0 &&
        onLocationMapGenerate &&
        steps &&
        steps[currentStep] &&
        steps[currentStep].key !== 'uploadDocument'
      ) {
        onLocationMapGenerate(locationMap, locationReference || {})
      }

      const isPropReferenceEmpty =
        !props_reference ||
        (typeof props_reference === 'object' &&
          Object.keys(props_reference).length === 0)

      if (
        !hasPendingDraft &&
        isPropReferenceEmpty &&
        propMap &&
        Object.keys(propMap).length > 0 &&
        data.shot_list &&
        data.shot_list.length > 0 &&
        steps &&
        steps[currentStep] &&
        steps[currentStep].key !== 'uploadDocument'
      ) {
        // Automatically trigger prop generation if missing
        if (typeof data.prop_map === 'string' || typeof propMap === 'object') {
          // VideoAi.js effect will handle it, but we can also trigger here if needed
          // For now, VideoAi.js effect is safer as it has access to the generatePropMap function
        }
      }
    } catch (error) {
      console.error('Error fetching video data:', error)
      if (error?.response?.status === 404) {
        setError('NOT_FOUND')
      } else {
        setError('GENERIC')
      }
    } finally {
      setIsFetchingVideoData(false)
    }
  }, [
    videoId,
    isFetchingVideoData,
    parseJSONSafely,
    normalizeValue,
    flatShots,
    setFormData,
    setStepsValidation,
    onLocationMapGenerate
  ])

  /**
   * Create new video
   */
  const handleCreateNew = useCallback(
    async data => {
      if (_.isEqual(data, defaultData)) return

      try {
        setIsSavingVideoData(true)
        const payload = getPayload(data)
        const response = await createAiVideo({
          voiceoverText: payload.voiceoverText || ''
        })
        const newVideoId = response && response.data && response.data.id
        if (newVideoId) {
          await updateAiVideo(newVideoId, payload)
        }
        const stepKey = steps[currentStep]?.key || 'uploadDocument'
        history.push(`/video-create/${newVideoId}/${stepKey}`)
      } catch (error) {
        console.error('Error creating new video:', error)
      } finally {
        setIsSavingVideoData(false)
      }
    },
    [getPayload, steps, currentStep]
  )

  /**
   * Update existing video
   */
  const handleUpdateVideoData = useCallback(
    async data => {
      if (_.isEqual(data, defaultData) || !videoId || isFetchingVideoData)
        return
      try {
        setIsSavingVideoData(true)
        const payload = getPayload(data)
        await updateAiVideo(videoId, payload)
      } catch (error) {
        console.error('Error updating video data:', error)
      } finally {
        setIsSavingVideoData(false)
      }
    },
    [videoId, getPayload, isFetchingVideoData]
  )

  /**
   * Save video data (auto-determines create vs update)
   */
  const handleSaveVideoData = useCallback(
    _.debounce(async () => {
      if (saveInProgress.current || isFetchingVideoData) return

      // Only block creation if voiceoverText is empty. Updates should be allowed.
      if (!videoId && !formData.voiceoverText) return

      try {
        saveInProgress.current = true
        if (videoId) {
          await handleUpdateVideoData(formData)
        } else {
          await handleCreateNew(formData)
        }
      } finally {
        saveInProgress.current = false
      }
    }, 1000),
    [
      videoId,
      formData,
      handleUpdateVideoData,
      handleCreateNew,
      isFetchingVideoData
    ]
  )

  /**
   * Clone video with new style and/or new voice
   */
  const cloneVideoWithStyle = useCallback(
    async (newStyle, newVoice = null, type = 'video_style') => {
      try {
        setIsCloning(true)
        const payload = getPayload(formData)

        // Apply new style if provided
        if (newStyle) {
          const styleId =
            newStyle.id ||
            newStyle.raw?.id ||
            newStyle.key ||
            (typeof newStyle === 'string' ? newStyle : null)

          if (type === 'shot_style') {
            payload.shotGenerationStyle = styleId
            payload.editingStyle = true
            // Clear shot list entirely as requested: "when cloning / overroding, it should remove the shotlist as well"
            payload.shotList = []
          } else {
            payload.videoStyle = newStyle
          }
        }

        // Apply new voice if provided
        if (newVoice) {
          payload.selected_voice = newVoice
        }

        // Clear generated assets
        if (payload.shotList && payload.shotList.length > 0) {
          payload.shotList = payload.shotList.map(scene => ({
            ...(newVoice
              ? clearSceneCurrentAudio(scene, {
                  audio_duration_estimated: true,
                  audio_history: []
                })
              : normalizeSceneAudioHistory(scene)),
            // Clear audio if voice is changing
            shots: scene.shots.map(shot => ({
              ...shot,
              // Clear images if style is changing
              ...(newStyle &&
                type === 'video_style' && {
                imageSrc: null
              }),
              // Always clear video-related fields when cloning with changes
              vidSrc: null,
              video_duration: null,
              // Clear audio duration on shots if voice is changing
              ...(newVoice && {
                audio_duration: null
              })
            }))
          }))
        }

        payload.video_url = null
        payload.final_videos_history = '[]'
        payload.transitions = '[]'
        payload.character_voices = JSON.stringify(
          formData.characterVoices || {}
        )

        const response = await createAiVideo({
          voiceoverText: payload.voiceoverText || ''
        })
        const newVideoId = response && response.data && response.data.id

        if (newVideoId) {
          console.log("test newVideoId", payload)

          await updateAiVideo(newVideoId, payload)
        }

        return newVideoId
      } catch (error) {
        console.error('Error cloning video:', error)
        throw error
      } finally {
        setIsCloning(false)
      }
    },
    [formData, getPayload]
  )

  /**
   * Reset form data to defaults
   */
  const resetFormData = useCallback(() => {
    setFormData(defaultData)
  }, [setFormData])

  return {
    // State
    isFetchingVideoData,
    isSavingVideoData,
    isCloning,
    error,

    // Actions
    fetchVideoData,
    handleSaveVideoData,
    handleCreateNew,
    handleUpdateVideoData,
    cloneVideoWithStyle,
    resetFormData,

    // Utilities
    parseJSONSafely,
    getPayload,
    flatShots,

    // Constants
    defaultData
  }
}

export default useVideoDataPersistence
